import * as React from 'react';
import { Link } from 'react-router-dom';
import * as Models from '../../../store/Models/SharedMaterials/DataModel';
import { ToxnotReactComponentPaths } from '../../core/ToxnotReactComponentPaths';
import { SharedMaterialNotFound } from './SharedMaterialNotFound';

import './SharedMaterialDetail.css';

import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { defineDetailSections, ViewDetailsRenderer } from './SharedMaterialDetailDataSubsection';
import { LoadingSpinner, LoadingSpinnerSize } from '../../core/LoadingSpinner';

export enum PublishMode {
    Single = "Single",
    Multiple = "Multiple"
}

export interface DetailSectionRenderData {
    coreData: Models.SharedMaterialCoreData;
    detailBundle: Models.SharedMaterialDetailResponse;
    company: Models.SharedMaterialsCatalogCompany;
}

export abstract class AbstractSharedMaterialDetail<P extends ToxnotComponentProperties> extends ToxnotComponent<P> implements ViewDetailsRenderer {
    protected abstract isLoading(): boolean;

    protected wrapBody(body: JSX.Element): JSX.Element {
        return body;
    }

    protected abstract peekRenderData(): DetailSectionRenderData;

    protected abstract peekCollapsedSections(): string[];

    protected abstract renderTopLevelActionButtons(data: DetailSectionRenderData): JSX.Element | null;

    protected abstract renderModals(): JSX.Element;

    // ===============================================================================

    private renderBody(data: DetailSectionRenderData): JSX.Element {

        const topLevelActionButtons = this.renderTopLevelActionButtons(data);
        return (
            <div className="sm2-browser-header">
                {null !== topLevelActionButtons &&
                    <div className="tn-section-row">
                        {topLevelActionButtons}
                    </div>
                }
                {this.renderImageAndDetailsContainer(data)}
                <div className="tn-section-row">
                    <div className="tn-section-col-12">
                        <div className="shared-material-data-container">
                            {((data.coreData.accessLevel === Models.SharedMaterialAccessType.Public) || data.coreData.hasAccess)
                                ? this.renderAllSharedMaterialDataSections(data)
                                : this.renderAccessDeniedInfoBar()
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public render() {
        if (this.isLoading()) {
            return <div className="loading-message">
                <LoadingSpinner size={LoadingSpinnerSize.Medium} />
            </div>;
        }
        else if (!this.isSharedMaterialFound()) {
            return <SharedMaterialNotFound appServices={this.props.appServices} />
        }

        return this.wrapBody(
            <>
                <div className="tn-section public-material-section">
                    {this.renderBody(this.peekRenderData())}
                </div>
                {this.renderModals()}
            </>
        );
    }

    private renderImageAndDetailsContainer(data: DetailSectionRenderData) {
        const coreData = data.coreData;
        const detailData = data.detailBundle!.detailData;
        const supplier = data.company;
        const lastUpdatedDateStringLocalized = new Date(coreData.lastModified).toLocaleDateString();
        const sharedMaterialDetailsClassName = `tn-section-col-${coreData.materialPhotoFileId === null ? 12 : 8} shared-material-details`;
        const isUnClaimed = data.detailBundle.isUnClaimed;
        return (
            <div className="tn-section-row margin-bottom">
                <div className="tn-section-col-4">
                    {coreData.materialPhotoFileId !== null &&
                        <div className="material-image-container">
                            <img src={`/Api/GeneralFileImage?fileId=${encodeURIComponent(coreData.materialPhotoFileId)}&overrideTeamOwnerId=${encodeURIComponent(supplier.ownerId)}`} />
                        </div>}
                </div>

                <div className={sharedMaterialDetailsClassName}>
                    <h2 className="shared-material-name">{coreData.materialName}</h2>
                    <div className="supplier-info-container">
                        <div className="supplier-name-and-logo">
                            <p className="shared-material-supplier-name">{supplier.name}</p>
                            {(supplier.logoFileId !== null) && <img src={`/Api/GeneralFileImage?fileId=${encodeURIComponent(supplier.logoFileId)}&overrideTeamOwnerId=${encodeURIComponent(supplier.ownerId)}`} className="shared-material-manufacturer-logo" />}
                        </div>
                        <div className="supplier-action-buttons-container">
                            {(!isUnClaimed) &&
                                <Link role="button" to={ToxnotReactComponentPaths.ContactSupplier + `/${encodeURIComponent(supplier.companyId)}/${encodeURIComponent(coreData.sharedMaterialId)}`} onClick={this.contactSupplierButton_click} id="contact-supplier-button" className="btn btn-default ">{this.localizeString("Contact Company")}</Link>}
                            <Link role="button" to={ToxnotReactComponentPaths.SharedMaterialSupplierBase + `/${encodeURIComponent(supplier.ownerId)}/${encodeURIComponent(supplier.companyId)}`} className="btn btn-default" onClick={this.viewCompanyButton_click}>{this.localizeString("View Company's Page")}</Link>
                            {("" !== detailData.general.purchaseUrl && !isUnClaimed) && 
                            <a role="button" href={detailData.general.purchaseUrl} id="purchase-material-button" className="btn btn-default" target="_blank" rel="noopener noreferrer" onClick={this.purchaseMaterial_click}>{this.localizeString("Purchase Material")} </a>}
                            <br />
                        </div>
                    </div>
                    <div className="shared-material-description-container">
                        {detailData.general.description.split('\n').map((s, index) => (
                            <p key={index}>
                                {s.trim()}
                            </p>
                        ))}
                    </div>
                    <div className="passport-details-category">
                        {this.localizeString("Category")}: {coreData.categories.join(", ")}
                    </div>
                    {isUnClaimed && (
                        <p>{this.localizeString("Curated by 3E")} </p>
                    )}
                    <p className="last-updated">
                        {this.localizeString("Last updated on")} {lastUpdatedDateStringLocalized}
                    </p>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderAllSharedMaterialDataSections(data: DetailSectionRenderData) {
        if (this.isLoading()) {
            return null;
        }

        const detailSections = defineDetailSections(this.props.appServices, data.detailBundle.dynamicRegulationTable);

        const items: JSX.Element[] = [];

        const coreData = data.coreData;
        const detailBundle = data.detailBundle!;

        // sort any sections without data to the bottom
        const restrictedData = detailBundle.detailData.restrictedData!;
        const sortedSections = detailSections.sort((a, b) => {
            const aHasData = a.hasData(coreData, restrictedData);
            const bHasData = b.hasData(coreData, restrictedData);
            return (aHasData && bHasData) ? 0 : (aHasData ? -1 : 1);
        });

        for (let section of sortedSections) {
            const isExpanded = section.isExpanded(this.peekCollapsedSections());
            const hasData = section.hasData(coreData, restrictedData)
            items.push(
                <div className="group" key={section.dataSectionId}>
                    {hasData
                        ? <div className="title with-data" onClick={e => this.toggleSection_click(section.dataSectionId)}><span className={"glyphicon glyphicon-chevron-" + (isExpanded ? "up" : "down")}></span> {section.title}</div>
                        : <div className="title no-section-data">{section.title} <span className="no-data-tag">{this.localizeString("(No Data)")}</span></div>
                    }
                    {isExpanded && hasData && section.render(coreData, restrictedData, detailBundle.fileTable, this, data.company.ownerId)}
                </div>
            )
        }

        return <div className="grouped-data">{items}</div>;
    }

    // ===============================================================================

    protected isSharedMaterialFound() {
        return true;
    }

    // ===============================================================================

    private renderAccessDeniedInfoBar() {
        return (
            <div className="alert alert-warning">
                {this.localizeString("This material's data is available upon request.")}{" "}<a role="button" onClick={this.requestAccessButton_click}>{this.localizeString("Click here to request access.")}</a>
            </div>
        );
    }

    // ===============================================================================

    /**
     * Provides specific attributes to the element responsible for handling the various View Details redirects.
     */
    abstract renderViewDetailsAttributes: (inventoryId: string, publicInventoryId: string, publicInventoryDataIdId: string, controllerId: string, contentContext: string) => { [attribute: string]: any };
    abstract addToLibraryButton_click: (sharedMaterialId: string) => void;

    // ===============================================================================

    //
    // UI Events
    //

    // ===============================================================================

    protected abstract toggleSection_click: (dataSectionId: string) => void;

    protected abstract contactSupplierButton_click: (e: React.MouseEvent) => void;

    protected abstract viewCompanyButton_click: (e: React.MouseEvent) => void;

    protected abstract purchaseMaterial_click: (e: React.MouseEvent) => void;

    protected abstract requestAccessButton_click: (e: React.MouseEvent) => void;
}
